import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    Select,
    MenuItem,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Grid,
    Paper,
    Collapse,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getRoles, getOrganizations, createUser, updateUser, getUserById } from '../../api/api';

const AddOrEditUser = () => {
    const navigate = useNavigate();
    const { userId } = useParams(); // Extract userId from the URL
    const [isEditMode, setIsEditMode] = useState(false);
    const [roles, setRoles] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '', // Empty for edit mode unless explicitly updated
        roles: [],
        organization: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: {
            street: '',
            city: '',
            state: '',
            zip: '',
            country: '',
        },
    });
    const [showAddress, setShowAddress] = useState(false);

    // Fetch roles, organizations, and user data (if editing)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [rolesResponse, orgsResponse] = await Promise.all([
                    getRoles({}),
                    getOrganizations({}),
                ]);
                setRoles(rolesResponse.data);
                setOrganizations(orgsResponse.data.data);

                if (userId) {
                    setIsEditMode(true);
                    const userResponse = await getUserById(userId); // Fetch user details by ID
                    setFormData((prev) => ({
                        ...prev,
                        ...userResponse.data,
                        roles: userResponse.data.roles.map((role) => role._id),
                        organization:  userResponse.data.organization ? userResponse.data.organization._id : '',
                    }));
                    if (
                        userResponse.data.address &&
                        Object.values(userResponse.data.address).some((val) => val)
                    ) {
                        setShowAddress(true); // Show address if user has address data
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                [name]: value,
            },
        }));
    };

    const handleSubmit = async () => {
        if (!formData.organization) {
            alert('Organization is required!');
            return;
        }

        try {
            if (isEditMode) {
                await updateUser(userId, formData); // Update user API call
                alert('User updated successfully!');
            } else {
                await createUser(formData); // Create user API call
                alert('User created successfully!');
            }
            navigate('/users');
        } catch (error) {
            alert(isEditMode ? 'Error updating user!' : 'Error creating user!');
            console.error('Error:', error);
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            bgcolor="#f4f4f4"
            p={2}
        >
            <Paper elevation={3} sx={{ padding: 4, width: '400px', borderRadius: '8px' }}>
                <Typography variant="h5" textAlign="center" gutterBottom>
                    {isEditMode ? 'Edit User' : 'Add User'}
                </Typography>
                <Grid container spacing={2}>
                    {/* Username */}
                    <Grid item xs={12}>
                        <TextField
                            label="Username"
                            name="username"
                            fullWidth
                            required
                            value={formData.username}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            name="email"
                            type="email"
                            fullWidth
                            required
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Password */}
                    {!isEditMode && (
                        <Grid item xs={12}>
                            <TextField
                                label="Password"
                                name="password"
                                type="password"
                                fullWidth
                                required
                                onChange={handleChange}
                            />
                        </Grid>
                    )}

                    {/* First Name */}
                    <Grid item xs={6}>
                        <TextField
                            label="First Name"
                            name="firstName"
                            fullWidth
                            value={formData.firstName}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Last Name */}
                    <Grid item xs={6}>
                        <TextField
                            label="Last Name"
                            name="lastName"
                            fullWidth
                            value={formData.lastName}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Phone */}
                    <Grid item xs={12}>
                        <TextField
                            label="Phone"
                            name="phone"
                            fullWidth
                            value={formData.phone}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Roles */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="roles-label">Roles</InputLabel>
                            <Select
                                labelId="roles-label"
                                name="roles"
                                multiple
                                value={formData.roles}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        roles: [...e.target.value],
                                    }))
                                }
                            >
                                {roles.map((role) => (
                                    <MenuItem key={role._id} value={role._id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Organization */}
                    <Grid item xs={12}>
                        <FormControl fullWidth required>
                            <InputLabel id="org-label">Organization</InputLabel>
                            <Select
                                labelId="org-label"
                                name="organization"
                                value={formData.organization}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        organization: e.target.value,
                                    }))
                                }
                            >
                                {organizations.map((org) => (
                                    <MenuItem key={org._id} value={org._id}>
                                        {org.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Toggle Address Section */}
                <Box mt={3}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setShowAddress(!showAddress)}
                    >
                        {showAddress ? 'Hide Address' : 'Add Address'}
                    </Button>
                    <Collapse in={showAddress}>
                        <Grid container spacing={2} mt={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Street"
                                    name="street"
                                    fullWidth
                                    value={formData.address.street}
                                    onChange={handleAddressChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="City"
                                    name="city"
                                    fullWidth
                                    value={formData.address.city}
                                    onChange={handleAddressChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="State"
                                    name="state"
                                    fullWidth
                                    value={formData.address.state}
                                    onChange={handleAddressChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Zip Code"
                                    name="zip"
                                    fullWidth
                                    value={formData.address.zip}
                                    onChange={handleAddressChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Country"
                                    name="country"
                                    fullWidth
                                    value={formData.address.country}
                                    onChange={handleAddressChange}
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                </Box>

                {/* Buttons */}
                <Box mt={3} display="flex" justifyContent="space-between">
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => navigate('/users')}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                    >
                        {isEditMode ? 'Update User' : 'Add User'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default AddOrEditUser;
